/**
 * useSessionLifecycle — derives a session's live phase from clock + status.
 *
 * Returns:
 *   phase: one of 'scheduled' | 'rsvp_open' | 'locked' | 'live' | 'ended' | 'completed' | 'cancelled' | 'needs_guests'
 *   isLive: boolean (true during the session window)
 *   isUpcoming: boolean (before session start)
 *   secondsToStart, secondsSinceStart, secondsToEnd: numeric helpers (null if not applicable)
 *   secondsToRsvpOpen: seconds until rsvp_opens_at (null if no RSVP or already open)
 *
 * Auto-recomputes via the `tick` state so the UI feels alive:
 *   - Default: 60s tick (frugal)
 *   - When the next milestone (RSVP open / session start) is < 1h away: 1s tick
 *     so the countdown drums down with seconds — iter112.
 *
 * Live window math:
 *   sessionStart = combine(date, start_time) in user's local timezone
 *   sessionEnd   = sessionStart + duration_minutes (default 120)
 *   isLive       = sessionStart <= now <= sessionEnd  AND  status not in [cancelled, completed]
 */
import { useEffect, useState, useMemo } from 'react';
import { serverNow, syncServerClock } from '../lib/serverClock';

const DEFAULT_DURATION_MIN = 120;
const ONE_HOUR_SECONDS = 3600;

function buildSessionStart(dateStr, timeStr) {
  if (!dateStr) return null;
  const [yy, mo, dd] = dateStr.split('-').map((x) => parseInt(x, 10) || 0);
  const [hh, mm] = (timeStr || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
  if (!yy || !mo || !dd) return null;
  // Local wall-clock — matches the way frontend creates sessions (uses local date input).
  return new Date(yy, mo - 1, dd, hh, mm, 0, 0);
}

export default function useSessionLifecycle(session, options = {}) {
  const duration = options.durationMinutes || session?.duration_minutes || DEFAULT_DURATION_MIN;
  const [tick, setTick] = useState(0);
  const [fastTick, setFastTick] = useState(false);

  // iter265 — sync to server clock once so time-gated phases flip on SERVER time
  useEffect(() => { syncServerClock(); }, []);

  // Adaptive ticker — switch to 1s cadence when the next milestone is < 1h away.
  useEffect(() => {
    // Compute "is fast needed?" once per tick based on current `session`.
    const computeFast = () => {
      if (!session) return false;
      if (session.status === 'cancelled' || session.status === 'completed') return false;
      const now = serverNow();
      const candidates = [];
      if (session.rsvp_opens_at) {
        const d = new Date(session.rsvp_opens_at).getTime() - now;
        if (d > 0) candidates.push(d / 1000);
      }
      const sessionStart = buildSessionStart(session.date, session.start_time);
      if (sessionStart) {
        const d = (sessionStart.getTime() - now) / 1000;
        if (d > 0) candidates.push(d);
      }
      if (!candidates.length) return false;
      return Math.min(...candidates) <= ONE_HOUR_SECONDS;
    };
    setFastTick(computeFast());
  }, [session, tick]);

  useEffect(() => {
    const intervalMs = fastTick ? 1_000 : 60_000;
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [fastTick]);

  return useMemo(() => {
    const result = {
      phase: 'scheduled',
      isLive: false,
      isUpcoming: false,
      isEnded: false,
      secondsToStart: null,
      secondsSinceStart: null,
      secondsToEnd: null,
      secondsToRsvpOpen: null,
    };
    if (!session) return result;

    // Terminal states first (clock irrelevant)
    if (session.status === 'cancelled') return { ...result, phase: 'cancelled' };
    if (session.status === 'completed') return { ...result, phase: 'completed' };

    const now = new Date(serverNow());
    // Compute secondsToRsvpOpen up-front so it's always populated when relevant.
    let secondsToRsvpOpen = null;
    if (session.rsvp_opens_at) {
      const d = new Date(session.rsvp_opens_at) - now;
      if (d > 0) secondsToRsvpOpen = Math.round(d / 1000);
    }

    const sessionStart = buildSessionStart(session.date, session.start_time);
    if (!sessionStart) {
      // Without a parseable start, fall back to status flag.
      return {
        ...result,
        phase: session.status === 'rsvp_open' ? 'rsvp_open' : 'scheduled',
        secondsToRsvpOpen,
      };
    }
    const sessionEnd = new Date(sessionStart.getTime() + duration * 60_000);
    const secondsToStart = Math.round((sessionStart - now) / 1000);
    const secondsSinceStart = Math.round((now - sessionStart) / 1000);
    const secondsToEnd = Math.round((sessionEnd - now) / 1000);

    if (now >= sessionStart && now <= sessionEnd) {
      return {
        phase: 'live',
        isLive: true,
        isUpcoming: false,
        isEnded: false,
        secondsToStart: 0,
        secondsSinceStart,
        secondsToEnd,
        secondsToRsvpOpen: null,
      };
    }
    if (now > sessionEnd) {
      return {
        phase: 'ended',
        isLive: false,
        isUpcoming: false,
        isEnded: true,
        secondsToStart: 0,
        secondsSinceStart,
        secondsToEnd: 0,
        secondsToRsvpOpen: null,
      };
    }
    // Pre-start. Differentiate scheduled vs rsvp_open vs locked vs needs_guests.
    let phase = 'scheduled';
    if (session.status === 'needs_guests') phase = 'needs_guests';
    else if (session.status === 'rsvp_open') phase = 'rsvp_open';
    else if (session.status === 'rsvp_closed') phase = 'locked';
    // iter265 — timestamp-derived open: don't wait for the 60s cron to flip
    // `status`. The instant rsvp_opens_at passes (server clock), the UI opens.
    if (phase === 'scheduled' && session.rsvp_opens_at && !session.rsvp_locked) {
      const opensMs = new Date(session.rsvp_opens_at).getTime();
      const closesMs = session.rsvp_closes_at ? new Date(session.rsvp_closes_at).getTime() : null;
      if (now.getTime() >= opensMs && (!closesMs || now.getTime() <= closesMs)) {
        phase = 'rsvp_open';
        secondsToRsvpOpen = null;
      }
    }
    return {
      phase,
      isLive: false,
      isUpcoming: true,
      isEnded: false,
      secondsToStart,
      secondsSinceStart: null,
      secondsToEnd,
      secondsToRsvpOpen,
    };
  }, [session, duration, tick]);
}

/** Format seconds → "1h 23m" / "23m" / "45s" — for elapsed/countdown chips. */
export function formatDuration(sec) {
  if (sec == null) return '';
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * iter112 — Adaptive countdown formatter. Shows seconds only when the
 * milestone is close enough that they matter:
 *   > 1h        → "1h 56m"            (no seconds; ticks every minute)
 *   1m – 60m    → "47m 23s"           (seconds appear; drama builds)
 *   < 1m        → "23s"
 *   = 0         → "any moment"
 * The hook flips to a 1s ticker as soon as the milestone is < 1h.
 */
export function formatCountdown(sec) {
  if (sec == null) return '';
  const s = Math.max(0, Math.round(sec));
  if (s === 0) return 'any moment';
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${m}m ${String(ss).padStart(2, '0')}s`;
  }
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}
